import { useAuth0 } from '@auth0/auth0-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  Loader,
  Banner,
  Breadcrumbs,
  Button,
  Combobox,
  Table,
  LayerCard,
  Empty,
  Pagination,
} from '@cloudflare/kumo'
import { ClipboardTextIcon, HouseIcon, ExportIcon } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useHasRole } from '../auth/useRoles'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import {
  OCSF_CLASS_LABEL,
  OCSF_ACTIVITY_LABEL,
  OCSF_STATUS_LABEL,
  type OcsfQueryRow,
  type ActorIdentity,
  type Coworker,
  type Company,
} from '@hub-wifi/shared'

// Event-type ids this app actually emits (Task 7 spec). 3002 gets a
// device-specific label below — it is the one option this filter exists
// for ("only see the events received from the device"), emitted by exactly
// one code path (apps/api/src/routes/deviceMonitor.ts) — so `class_uid=3002`
// is a precise "door access events received from the device" filter. The
// others keep the generic OCSF_CLASS_LABEL name.
const EVENT_TYPE_CLASS_UIDS = [3002, 3001, 3004, 6003] as const

interface ApiResponse {
  events: OcsfQueryRow[]
  identities: Record<string, ActorIdentity>
}

/** A `Combobox` option — `Combobox`'s `value`/`onValueChange` carry this
 * whole object (not a bare id), unlike `Select`. */
interface FilterOption {
  value: string
  label: string
}

/**
 * Resolve an OCSF actor `uid` into a display-friendly name/email/company,
 * falling back to the raw `uid` plus any `unmapped.domain` hint when the
 * identity could not be resolved server-side.
 */
export function resolveActorDisplay(
  uid: string,
  identities: Record<string, ActorIdentity>,
  envelope: Pick<OcsfQueryRow['envelope'], 'unmapped'>,
): { primary: string; secondary: string | null } {
  const id = identities[uid]
  if (id) {
    const parts = [id.email, id.companyName].filter(Boolean)
    return { primary: id.name, secondary: parts.length ? parts.join(' · ') : null }
  }
  const domain = envelope.unmapped?.domain
  return { primary: uid, secondary: typeof domain === 'string' ? domain : null }
}

type AuditTab = 'all' | 'failures'

/** OCSF `status_id` value that marks a failed activity. */
const OCSF_STATUS_FAILURE = 2

/** RFC 4180 CSV field escaping: quote fields containing comma, quote or newline. */
function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

// TODO(review): consider role-tier redaction on operator view (reported by
// ring:security-reviewer on 2026-05-24, severity: Medium). Operators currently
// see the same envelope payload admins see. We accept this for the rollout
// because (a) `requireRole('admin', 'operator')` already gates the underlying
// /api/audit/events endpoint to privileged internal staff, (b) emit-side
// scrubbing in audit.ts strips `error.message` for PII_BEARING_CODES, and
// (c) companyUpdate / buildingUpdate detail fields are scrubbed before they
// hit the DO. Re-evaluate when external/customer roles gain audit access.
export function AuditLogPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t, locale } = useTranslation()
  const isAdmin = useHasRole('admin')
  const [events, setEvents] = useState<OcsfQueryRow[]>([])
  const [identities, setIdentities] = useState<Record<string, ActorIdentity>>({})
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [expandedUid, setExpandedUid] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(25)
  const [activeTab, setActiveTab] = useState<AuditTab>('all')

  // Filters — all default to '' ("All"). '' is omitted from the query
  // entirely rather than sent as an empty param (an empty `actor_uid=`
  // would be read by the API as a real, if odd, filter value).
  const [actorUid, setActorUid] = useState('')
  const [companyUid, setCompanyUid] = useState('')
  const [classUid, setClassUid] = useState('')
  const [coworkers, setCoworkers] = useState<Coworker[]>([])
  const [companies, setCompanies] = useState<Company[]>([])

  // Filter option lists — reuses the same list endpoints CoworkersPage/
  // CompaniesPage already call directly via apiRequest (no dedicated list
  // client exists for either). Best-effort: a failure here only means the
  // corresponding Select falls back to just "All", it doesn't block the
  // audit events themselves.
  useEffect(() => {
    async function loadFilterOptions() {
      try {
        const token = await getAccessTokenSilently()
        const [coworkerList, companyList] = await Promise.all([
          apiRequest<Coworker[]>('/api/coworkers', { token }),
          isAdmin ? apiRequest<Company[]>('/api/companies', { token }) : Promise.resolve([]),
        ])
        setCoworkers(coworkerList)
        setCompanies(companyList)
      } catch {
        // Best-effort — see comment above.
      }
    }
    void loadFilterOptions()
  }, [getAccessTokenSilently, isAdmin])

  // Combobox option lists. Coworker/company are sorted by label with "All"
  // pinned first (never sorted into the middle) — event type keeps its
  // deliberate manual order below, so the device option (3002) stays
  // reachable first.
  const coworkerOptions = useMemo<FilterOption[]>(() => {
    const sorted = coworkers
      .map((cw) => ({ value: cw.id, label: cw.name }))
      .sort((a, b) => a.label.localeCompare(b.label, locale))
    return [{ value: '', label: t('auditLog.filterAll') }, ...sorted]
  }, [coworkers, locale, t])

  const companyOptions = useMemo<FilterOption[]>(() => {
    const sorted = companies
      .map((co) => ({ value: co.id, label: co.name }))
      .sort((a, b) => a.label.localeCompare(b.label, locale))
    return [{ value: '', label: t('auditLog.filterAll') }, ...sorted]
  }, [companies, locale, t])

  // Event type — 3002 is emitted by exactly one code path
  // (deviceMonitor.ts), so it is an exact "events received from the
  // device" filter. No role gate: class_uid carries no cross-actor
  // risk, unlike actor_uid/company_uid above. Order is intentional
  // (All, then the device option, then the rest) — do NOT alphabetise.
  const eventTypeOptions = useMemo<FilterOption[]>(
    () => [
      { value: '', label: t('auditLog.filterAll') },
      ...EVENT_TYPE_CLASS_UIDS.map((id) => ({
        value: String(id),
        label:
          id === 3002 ? t('auditLog.eventTypeDoorAccess') : (OCSF_CLASS_LABEL[id] ?? String(id)),
      })),
    ],
    [t],
  )

  const selectedCoworkerOption =
    coworkerOptions.find((o) => o.value === actorUid) ?? coworkerOptions[0]
  const selectedCompanyOption =
    companyOptions.find((o) => o.value === companyUid) ?? companyOptions[0]
  const selectedEventTypeOption =
    eventTypeOptions.find((o) => o.value === classUid) ?? eventTypeOptions[0]

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: '500' })
    if (actorUid) params.set('actor_uid', actorUid)
    // company_uid is admin-only server-side (403 for operators) — guard it
    // here too so a stale selection can't fire a doomed request.
    if (isAdmin && companyUid) params.set('company_uid', companyUid)
    if (classUid) params.set('class_uid', classUid)
    return params.toString()
  }, [actorUid, companyUid, classUid, isAdmin])

  useEffect(() => {
    async function loadEvents() {
      setLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const data = await apiRequest<ApiResponse>(`/api/audit/events?${query}`, { token })
        setEvents(data.events)
        setIdentities(data.identities ?? {})
        setError(null)
        setPage(1)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
      } finally {
        setLoading(false)
      }
    }
    void loadEvents()
  }, [query, getAccessTokenSilently])

  // Client-side tab filter on the OCSF failure signal (status_id === 2).
  const filteredEvents =
    activeTab === 'failures'
      ? events.filter((evt) => evt.envelope.status_id === OCSF_STATUS_FAILURE)
      : events

  const pagedEvents = filteredEvents.slice((page - 1) * perPage, page * perPage)

  // Export the CURRENTLY FILTERED set (all pages), not just the visible page.
  function handleExportCsv() {
    const header = [
      t('auditLog.timestamp'),
      t('auditLog.operation'),
      t('auditLog.actor'),
      t('auditLog.status'),
      t('auditLog.resource'),
    ]
    const rows = filteredEvents.map((evt) => {
      const primaryResource = evt.envelope.resources?.[0]
      const operation =
        evt.envelope.api?.operation ??
        OCSF_ACTIVITY_LABEL[evt.envelope.activity_id] ??
        String(evt.envelope.activity_id)
      return [
        new Date(evt.time).toISOString(),
        operation,
        (() => {
          const d = resolveActorDisplay(evt.envelope.actor.user.uid, identities, evt.envelope)
          return d.secondary ? `${d.primary} (${d.secondary})` : d.primary
        })(),
        OCSF_STATUS_LABEL[evt.envelope.status_id] ?? String(evt.envelope.status_id),
        primaryResource ? `${primaryResource.type}/${primaryResource.uid}` : '',
      ]
    })
    const csv = [header, ...rows]
      .map((cols) => cols.map((col) => csvEscape(String(col))).join(','))
      .join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('auditLog.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('auditLog.title')}
        description={t('auditLog.description')}
        tabs={[
          { label: t('auditLog.tab.all'), value: 'all' },
          { label: t('auditLog.tab.failures'), value: 'failures' },
        ]}
        defaultTab={activeTab}
        onValueChange={(v) => {
          setActiveTab(v as AuditTab)
          setPage(1)
        }}
      >
        <Button
          variant="outline"
          size="sm"
          icon={<ExportIcon size={16} />}
          onClick={handleExportCsv}
          disabled={filteredEvents.length === 0}
        >
          {t('auditLog.exportCsv')}
        </Button>
      </PageHeader>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Combobox
          items={coworkerOptions}
          value={selectedCoworkerOption}
          onValueChange={(v) => {
            const opt = v as FilterOption | null
            setActorUid(opt?.value ?? '')
          }}
          isItemEqualToValue={(a, b) => a.value === b.value}
        >
          <Combobox.TriggerValue className="w-40" placeholder={t('auditLog.filterCoworker')} />
          <Combobox.Content>
            <Combobox.Input placeholder={t('auditLog.filterCoworker')} />
            <Combobox.Empty>{t('common.noResults')}</Combobox.Empty>
            <Combobox.List>
              {(item: FilterOption) => (
                <Combobox.Item key={item.value} value={item}>
                  {item.label}
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox>
        {/* Company filter is admin-only — the API 403s a `company_uid`
            query from an operator, who is already pinned to their own
            actor_uid. */}
        {isAdmin && (
          <Combobox
            items={companyOptions}
            value={selectedCompanyOption}
            onValueChange={(v) => {
              const opt = v as FilterOption | null
              setCompanyUid(opt?.value ?? '')
            }}
            isItemEqualToValue={(a, b) => a.value === b.value}
          >
            <Combobox.TriggerValue className="w-40" placeholder={t('auditLog.filterCompany')} />
            <Combobox.Content>
              <Combobox.Input placeholder={t('auditLog.filterCompany')} />
              <Combobox.Empty>{t('common.noResults')}</Combobox.Empty>
              <Combobox.List>
                {(item: FilterOption) => (
                  <Combobox.Item key={item.value} value={item}>
                    {item.label}
                  </Combobox.Item>
                )}
              </Combobox.List>
            </Combobox.Content>
          </Combobox>
        )}
        {/* Event type — 3002 is emitted by exactly one code path
            (deviceMonitor.ts), so it is an exact "events received from the
            device" filter. No role gate: class_uid carries no cross-actor
            risk, unlike actor_uid/company_uid above. */}
        <Combobox
          items={eventTypeOptions}
          value={selectedEventTypeOption}
          onValueChange={(v) => {
            const opt = v as FilterOption | null
            setClassUid(opt?.value ?? '')
          }}
          isItemEqualToValue={(a, b) => a.value === b.value}
        >
          <Combobox.TriggerValue className="w-40" placeholder={t('auditLog.filterEventType')} />
          <Combobox.Content>
            <Combobox.Input placeholder={t('auditLog.filterEventType')} />
            <Combobox.Empty>{t('common.noResults')}</Combobox.Empty>
            <Combobox.List>
              {(item: FilterOption) => (
                <Combobox.Item key={item.value} value={item}>
                  {item.label}
                </Combobox.Item>
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && filteredEvents.length === 0 && (
        <Empty
          icon={<ClipboardTextIcon size={48} className="text-kumo-inactive" />}
          title={activeTab === 'failures' ? t('auditLog.emptyFailures') : t('auditLog.empty')}
        />
      )}

      {!loading && !error && filteredEvents.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <div style={{ overflowX: 'auto', width: '100%' }}>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.Head>{t('auditLog.timestamp')}</Table.Head>
                    <Table.Head>{t('auditLog.class')}</Table.Head>
                    <Table.Head>{t('auditLog.activity')}</Table.Head>
                    <Table.Head>{t('auditLog.actor')}</Table.Head>
                    <Table.Head>{t('auditLog.resource')}</Table.Head>
                    <Table.Head>{t('auditLog.status')}</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {pagedEvents.map((evt) => {
                    const primaryResource = evt.envelope.resources?.[0]
                    const isExpanded = expandedUid === evt.uid
                    const toggle = () => setExpandedUid(isExpanded ? null : evt.uid)
                    return (
                      <Fragment key={evt.uid}>
                        <Table.Row
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          aria-controls={`audit-detail-${evt.uid}`}
                          onClick={toggle}
                          onKeyDown={(e: React.KeyboardEvent<HTMLTableRowElement>) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              toggle()
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <Table.Cell>{new Date(evt.time).toLocaleString(locale)}</Table.Cell>
                          <Table.Cell>
                            {OCSF_CLASS_LABEL[evt.envelope.class_uid] ?? evt.envelope.class_uid}
                          </Table.Cell>
                          <Table.Cell>
                            {OCSF_ACTIVITY_LABEL[evt.envelope.activity_id] ??
                              evt.envelope.activity_id}
                            {evt.envelope.api?.operation ? ` (${evt.envelope.api.operation})` : ''}
                          </Table.Cell>
                          <Table.Cell>
                            {(() => {
                              const d = resolveActorDisplay(
                                evt.envelope.actor.user.uid,
                                identities,
                                evt.envelope,
                              )
                              return (
                                <div>
                                  <div>{d.primary}</div>
                                  {d.secondary && (
                                    <div className="text-xs text-kumo-inactive">{d.secondary}</div>
                                  )}
                                </div>
                              )
                            })()}
                          </Table.Cell>
                          <Table.Cell>
                            {primaryResource
                              ? `${primaryResource.type}/${primaryResource.uid}`
                              : '-'}
                          </Table.Cell>
                          <Table.Cell>
                            {OCSF_STATUS_LABEL[evt.envelope.status_id] ?? evt.envelope.status_id}
                          </Table.Cell>
                        </Table.Row>
                        {isExpanded && (
                          <Table.Row id={`audit-detail-${evt.uid}`}>
                            <Table.Cell colSpan={6}>
                              <pre className="text-xs bg-kumo-recessed p-2 rounded overflow-x-auto">
                                {JSON.stringify(evt.envelope, null, 2)}
                                {evt.detail !== null && evt.detail !== undefined && (
                                  <>
                                    {'\n\n--- detail ---\n'}
                                    {JSON.stringify(evt.detail, null, 2)}
                                  </>
                                )}
                              </pre>
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </Fragment>
                    )
                  })}
                </Table.Body>
              </Table>
            </div>
          </LayerCard.Primary>
        </LayerCard>
      )}

      {!loading && !error && filteredEvents.length > 0 && (
        <div className="mt-2">
          <Pagination
            page={page}
            setPage={setPage}
            perPage={perPage}
            totalCount={filteredEvents.length}
          >
            <Pagination.Info>
              {({ pageShowingRange, totalCount }) =>
                t('auditLog.paginationInfo', {
                  range: pageShowingRange,
                  total: String(totalCount ?? filteredEvents.length),
                })
              }
            </Pagination.Info>
            <Pagination.PageSize
              value={perPage}
              onChange={(size) => {
                setPerPage(size)
                setPage(1)
              }}
              options={[25, 50, 100]}
              label={t('auditLog.perPage')}
            />
            <Pagination.Controls />
          </Pagination>
        </div>
      )}
    </div>
  )
}
